import DiffMatchPatch from 'diff-match-patch'

export interface DiffRenderOptions {
  type?: 'inline' | 'side-by-side'
  showOnlyChanges?: boolean
}

export class DiffRenderer {
  private dmp = new DiffMatchPatch()
  
  /**
   * Render inline diff with word-level changes
   */
  renderInline(original: string, suggested: string): string {
    const diffs = this.dmp.diff_main(original, suggested)
    this.dmp.diff_cleanupSemantic(diffs)
    
    let html = '<div class="inline-diff">'
    
    diffs.forEach(([op, text]) => {
      if (op === 0) {
        // Unchanged text
        html += `<span class="diff-equal">${this.escapeHtml(text)}</span>`
      } else if (op === -1) {
        // Deleted text
        html += `<span class="diff-delete">${this.escapeHtml(text)}</span>`
      } else if (op === 1) {
        // Added text
        html += `<span class="diff-insert">${this.escapeHtml(text)}</span>`
      }
    })
    
    html += '</div>'
    return html
  }
  
  /**
   * Render side-by-side diff
   */
  renderSideBySide(original: string, suggested: string): { left: string; right: string } {
    const diffs = this.dmp.diff_main(original, suggested)
    this.dmp.diff_cleanupSemantic(diffs)
    
    let leftHtml = '<div class="diff-panel-content">'
    let rightHtml = '<div class="diff-panel-content">'
    
    diffs.forEach(([op, text]) => {
      const escaped = this.escapeHtml(text)
      if (op === 0) {
        // Unchanged text
        leftHtml += `<span class="diff-equal">${escaped}</span>`
        rightHtml += `<span class="diff-equal">${escaped}</span>`
      } else if (op === -1) {
        // Deleted text (only in left)
        leftHtml += `<span class="diff-delete">${escaped}</span>`
        rightHtml += `<span class="diff-delete-placeholder"></span>`
      } else if (op === 1) {
        // Added text (only in right)
        leftHtml += `<span class="diff-insert-placeholder"></span>`
        rightHtml += `<span class="diff-insert">${escaped}</span>`
      }
    })
    
    leftHtml += '</div>'
    rightHtml += '</div>'
    
    return {
      left: leftHtml,
      right: rightHtml
    }
  }
  
  /**
   * Get statistics about the diff
   */
  getDiffStats(original: string, suggested: string): {
    additions: number
    deletions: number
    changes: number
  } {
    const diffs = this.dmp.diff_main(original, suggested)
    this.dmp.diff_cleanupSemantic(diffs)
    
    let additions = 0
    let deletions = 0
    
    diffs.forEach(([op, text]) => {
      const words = text.split(/\s+/).filter(w => w.length > 0)
      if (op === -1) {
        deletions += words.length
      } else if (op === 1) {
        additions += words.length
      }
    })
    
    return {
      additions,
      deletions,
      changes: additions + deletions
    }
  }
  
  /**
   * Get compact diff changes for display with context
   */
  getCompactDiff(original: string, suggested: string): {
    added: string[]
    removed: string[]
  } {
    // Check if the entire text was replaced
    const originalTrimmed = original.trim()
    const suggestedTrimmed = suggested.trim()
    
    // Calculate similarity ratio
    const diffs = this.dmp.diff_main(original, suggested)
    this.dmp.diff_cleanupSemantic(diffs)
    
    // Count total changed characters
    let totalChanged = 0
    let totalLength = Math.max(originalTrimmed.length, suggestedTrimmed.length)
    
    for (const [op, text] of diffs) {
      if (op !== 0) {
        totalChanged += text.length
      }
    }
    
    const changeRatio = totalLength > 0 ? totalChanged / totalLength : 1
    
    // If more than 80% changed, show as complete replacement
    if (changeRatio > 0.8) {
      const added: string[] = []
      const removed: string[] = []
      
      if (originalTrimmed) {
        removed.push(originalTrimmed.length > 100 
          ? originalTrimmed.substring(0, 100) + '...' 
          : originalTrimmed)
      }
      
      if (suggestedTrimmed) {
        added.push(suggestedTrimmed.length > 100 
          ? suggestedTrimmed.substring(0, 100) + '...' 
          : suggestedTrimmed)
      }
      
      return { added, removed }
    }
    
    // For smaller changes, show with context
    const added: string[] = []
    const removed: string[] = []
    const contextLength = 20 // Reduced context length
    const maxDisplayLength = 80 // Maximum length to display for each change
    
    // Collect changes
    let position = 0
    
    for (let i = 0; i < diffs.length; i++) {
      const [op, text] = diffs[i]
      
      if (op === -1 && text.trim()) { // Deleted text
        const trimmed = text.trim()
        
        // Get minimal context
        const beforeContext = i > 0 && diffs[i-1][0] === 0 
          ? diffs[i-1][1].slice(-contextLength).trim()
          : ''
        const afterContext = i < diffs.length - 1 && diffs[i+1][0] === 0
          ? diffs[i+1][1].slice(0, contextLength).trim()
          : ''
        
        // Format based on length and context
        let formatted = ''
        
        if (trimmed.length <= 30) {
          // Short change - show with context
          if (beforeContext) formatted = '...' + beforeContext.slice(-15) + ' '
          formatted += trimmed
          if (afterContext) formatted += ' ' + afterContext.slice(0, 15) + '...'
        } else if (trimmed.length <= maxDisplayLength) {
          // Medium change - show full text, minimal context
          formatted = trimmed
        } else {
          // Long change - truncate
          formatted = trimmed.substring(0, maxDisplayLength) + '...'
        }
        
        removed.push(formatted)
      } else if (op === 1 && text.trim()) { // Added text
        const trimmed = text.trim()
        
        // Get minimal context
        const beforeContext = i > 0 && diffs[i-1][0] === 0 
          ? diffs[i-1][1].slice(-contextLength).trim()
          : ''
        const afterContext = i < diffs.length - 1 && diffs[i+1][0] === 0
          ? diffs[i+1][1].slice(0, contextLength).trim()
          : ''
        
        // Format based on length and context
        let formatted = ''
        
        if (trimmed.length <= 30) {
          // Short change - show with context
          if (beforeContext) formatted = '...' + beforeContext.slice(-15) + ' '
          formatted += trimmed
          if (afterContext) formatted += ' ' + afterContext.slice(0, 15) + '...'
        } else if (trimmed.length <= maxDisplayLength) {
          // Medium change - show full text, minimal context
          formatted = trimmed
        } else {
          // Long change - truncate
          formatted = trimmed.substring(0, maxDisplayLength) + '...'
        }
        
        added.push(formatted)
      }
    }
    
    return { added, removed }
  }
  
  /**
   * Generate minimal diff HTML for compact display
   */
  renderCompactDiff(original: string, suggested: string): string {
    const { added, removed } = this.getCompactDiff(original, suggested)
    let html = '<div class="compact-diff">'
    
    if (removed.length > 0) {
      html += '<div class="diff-removed">'
      removed.forEach(text => {
        html += `<span class="diff-item">- ${this.escapeHtml(text)}</span>`
      })
      html += '</div>'
    }
    
    if (added.length > 0) {
      html += '<div class="diff-added">'
      added.forEach(text => {
        html += `<span class="diff-item">+ ${this.escapeHtml(text)}</span>`
      })
      html += '</div>'
    }
    
    html += '</div>'
    return html
  }
  
  /**
   * Render inline diff for inline view panels
   */
  renderInlineDiff(original: string, suggested: string): string {
    const diffs = this.dmp.diff_main(original, suggested)
    this.dmp.diff_cleanupSemantic(diffs)
    
    let html = '<div class="inline-diff-view">'
    
    diffs.forEach(([op, text]) => {
      if (op === 0) {
        // Unchanged text
        html += `<span class="diff-unchanged">${this.escapeHtml(text)}</span>`
      } else if (op === -1) {
        // Deleted text
        html += `<span class="diff-removed">${this.escapeHtml(text)}</span>`
      } else if (op === 1) {
        // Added text
        html += `<span class="diff-added">${this.escapeHtml(text)}</span>`
      }
    })
    
    html += '</div>'
    return html
  }
  
  /**
   * Check if there are any changes
   */
  hasChanges(original: string, suggested: string): boolean {
    return original !== suggested
  }
  
  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    // Preserve line breaks by converting them to <br> tags
    return div.innerHTML.replace(/\n/g, '<br>')
  }
}

export const diffRenderer = new DiffRenderer()